import axios from 'axios';
import { API_BASE_URL } from '../config/constants';
import api from './api';

export interface LoginHistoryItem {
  _id: string;
  userId: {
    _id: string;
    name: string;
    email: string;
  };
  ip: string;
  userAgent: string;
  device: string;
  browser: string;
  os: string;
  status: 'success' | 'failed';
  failReason?: string;
  createdAt: string;
}

export interface LoginHistoryResponse {
  data: LoginHistoryItem[];
  pagination: {
    total: number;
    page: number;
    limit: number;
    pages: number;
  };
}

export interface LoginStats {
  totalLogins: number;
  successfulLogins: number;
  failedLogins: number;
  uniqueUsers: number;
  uniqueIPs: number;
  browsers: { name: string; count: number }[];
  devices: { name: string; count: number }[];
  recentActivity: LoginHistoryItem[];
}

class UserActivityService {
  private baseURL = '/admin/user-activities';

  private getHeaders() {
    const token = localStorage.getItem('accessToken');
    return {
      'Content-Type': 'application/json',
      'Authorization': token ? `Bearer ${token}` : ''
    };
  }

  /**
   * Get all login history with pagination
   */
  async getAllLoginHistory(
    page: number = 1,
    limit: number = 20,
    filters?: {
      userId?: string;
      status?: string;
      fromDate?: string;
      toDate?: string;
    }
  ): Promise<LoginHistoryResponse> {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString()
    });

    if (filters?.userId) params.append('userId', filters.userId);
    if (filters?.status) params.append('status', filters.status);
    if (filters?.fromDate) params.append('startDate', filters.fromDate);
    if (filters?.toDate) params.append('endDate', filters.toDate);

    const response = await axios.get(`${API_BASE_URL}${this.baseURL}/history?${params.toString()}`, {
      headers: this.getHeaders()
    });

    const resData = response.data;
    return {
      data: resData.data || [],
      pagination: resData.pagination || { total: 0, page, limit, pages: 0 }
    };
  }

  /**
   * Get login statistics
   */
  async getLoginStats(): Promise<LoginStats> {
    const response = await axios.get(`${API_BASE_URL}${this.baseURL}/stats`, {
      headers: this.getHeaders()
    });

    return response.data?.data || response.data;
  }

  /**
   * Get user login history
   */
  async getUserLoginHistory(userId: string, page: number = 1, limit: number = 20): Promise<LoginHistoryResponse> {
    const response = await axios.get(`${API_BASE_URL}${this.baseURL}/user/${userId}?page=${page}&limit=${limit}`, {
      headers: this.getHeaders()
    });

    const resData = response.data;
    return {
      data: resData.data || [],
      pagination: resData.pagination || { total: 0, page, limit, pages: 0 }
    };
  }

  /**
   * Delete login history records
   */
  async deleteLoginHistory(olderThanDays?: number): Promise<{ deleted: number; message: string }> {
    const params = olderThanDays ? `?olderThanDays=${olderThanDays}` : '';
    const response = await api.delete(`${this.baseURL}/history${params}`);
    return (response as any)?.data || response;
  }

  /**
   * Format date
   */
  formatDate(dateString: string): string {
    return new Date(dateString).toLocaleString('fa-IR');
  }
}

export const userActivityService = new UserActivityService();
export default userActivityService;

